/**
 * City Search — Nominatim geocoding integration.
 * Căutare localități din România cu autocomplete.
 */

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
const DEBOUNCE_MS = 350;
const MIN_QUERY = 2;

let debounceTimer = null;
let abortCtrl = null;

/**
 * Caută localități în România via Nominatim.
 * @param {string} query - Textul de căutare
 * @returns {Promise<Array<{name: string, displayName: string, lat: number, lon: number, type: string}>>}
 */
export async function searchLocalities(query) {
  // Abort previous request
  if (abortCtrl) abortCtrl.abort();
  abortCtrl = new AbortController();

  const params = new URLSearchParams({
    q: query,
    format: 'jsonv2',
    countrycodes: 'ro',
    limit: '8',
    addressdetails: '1',
    'accept-language': 'ro',
  });

  const resp = await fetch(`${NOMINATIM_URL}?${params}`, {
    signal: abortCtrl.signal,
    headers: { 'User-Agent': 'CAMS-Explorer/1.0' },
  });

  if (!resp.ok) throw new Error(`Nominatim: ${resp.status}`);

  const data = await resp.json();

  return data
    .filter(r => {
      // Filtrează doar localități reale (nu drumuri, ape, etc.)
      const validTypes = ['city', 'town', 'village', 'hamlet', 'municipality',
                          'suburb', 'neighbourhood', 'borough', 'administrative'];
      const validClasses = ['place', 'boundary'];
      return validClasses.includes(r.class) || validTypes.includes(r.type);
    })
    .map(r => {
      // Construiește un nume mai curat
      const addr = r.address || {};
      const placeName = addr.city || addr.town || addr.village || addr.hamlet
                     || addr.municipality || addr.suburb || r.name || query;
      const county = addr.county || addr.state || '';

      return {
        name: placeName,
        displayName: r.display_name,
        county,
        lat: parseFloat(r.lat),
        lon: parseFloat(r.lon),
        type: r.type,
        osmType: r.osm_type,
      };
    })
    // Deduplicate by name
    .filter((item, idx, arr) => arr.findIndex(x => x.name === item.name && x.county === item.county) === idx);
}

/**
 * Inițializează căutarea — event listeners pe input.
 * @param {function} onSelect - Callback(result) apelat la selectarea unui rezultat
 */
export function initCitySearch(onSelect) {
  const input = document.getElementById('city-search-input');
  const results = document.getElementById('city-search-results');
  if (!input || !results) return;

  // Debounced input
  input.addEventListener('input', () => {
    const q = input.value.trim();

    if (q.length < MIN_QUERY) {
      hideResults();
      return;
    }

    clearTimeout(debounceTimer);
    showLoading();

    debounceTimer = setTimeout(async () => {
      try {
        const items = await searchLocalities(q);
        renderResults(items, onSelect);
      } catch (err) {
        if (err.name === 'AbortError') return; // Canceled — ignore
        console.warn('Search error:', err);
        showNoResults('Eroare căutare');
      }
    }, DEBOUNCE_MS);
  });

  // Escape → close
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      input.value = '';
      hideResults();
      input.blur();
    }
  });

  // Click outside → close
  document.addEventListener('click', (e) => {
    const wrap = document.getElementById('city-search-wrap');
    if (wrap && !wrap.contains(e.target)) {
      hideResults();
    }
  });
}

// ── Render ────────────────────────────────────────────────────────

function renderResults(items, onSelect) {
  const results = document.getElementById('city-search-results');
  if (!results) return;

  if (items.length === 0) {
    showNoResults('Nicio localitate găsită');
    return;
  }

  const pinSvg = `<svg class="city-search-result-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
    <circle cx="12" cy="9" r="2.5"/>
  </svg>`;

  results.innerHTML = items.map(item => `
    <button class="city-search-result" data-lat="${item.lat}" data-lon="${item.lon}" data-name="${item.name}">
      ${pinSvg}
      <span class="city-search-result-name">${item.name}</span>
      <span class="city-search-result-detail">${item.county}</span>
    </button>
  `).join('');

  results.classList.remove('hidden');

  // Click handlers
  results.querySelectorAll('.city-search-result').forEach(btn => {
    btn.addEventListener('click', () => {
      const result = {
        name: btn.dataset.name,
        lat: parseFloat(btn.dataset.lat),
        lon: parseFloat(btn.dataset.lon),
      };

      // Update input and close
      const input = document.getElementById('city-search-input');
      if (input) input.value = '';
      hideResults();

      onSelect(result);
    });
  });
}

function showLoading() {
  const results = document.getElementById('city-search-results');
  if (!results) return;
  results.innerHTML = '<div class="city-search-status loading">Se caută</div>';
  results.classList.remove('hidden');
}

function showNoResults(msg) {
  const results = document.getElementById('city-search-results');
  if (!results) return;
  results.innerHTML = `<div class="city-search-status">${msg}</div>`;
  results.classList.remove('hidden');
}

function hideResults() {
  const results = document.getElementById('city-search-results');
  if (results) results.classList.add('hidden');
}
